import { z } from "zod/v4";

/** Agent choices are bounded design decisions, never executable layout/filter code. */
export const reelArtDirectionSchema = z.object({
  composition: z.enum(["center-statement", "editorial-left", "lower-third"]),
  pacing: z.enum(["brisk", "measured", "reflective"]),
  transition: z.enum(["dissolve", "cut", "slide"]),
  music: z.enum(["warm-pad", "minimal-pulse", "bright-keys"]),
  accent: z.enum(["scribble", "underline", "rule", "none"]),
  rationale: z.string().min(1).max(400),
});
// Older metadata remains readable; new AI outputs can only select centered titles.
export const reelGenerationArtDirectionSchema = reelArtDirectionSchema.extend({
  composition: z.literal("center-statement"),
  accent: z.enum(["scribble", "underline", "none"]),
});
export type ReelArtDirection = z.infer<typeof reelArtDirectionSchema>;
export const REEL_DIRECTION_OPTIONS = {
  composition: {
    "center-statement":
      "Centered bold statement at mid-frame; direct questions or contrasts",
  },
  pacing: {
    brisk: "14s / 2.3s per intro beat",
    measured: "16s / 2.6s per intro beat",
    reflective: "18s / 3s per intro beat",
  },
  transition: {
    dissolve: "Soft crossfades",
    cut: "Clean direct scene cuts",
    slide: "Restrained lateral scene reveal and type entry",
  },
  music: {
    "warm-pad": "Slow sustained instrumental",
    "minimal-pulse": "Restrained rhythmic pulse",
    "bright-keys": "Light plucked-key melody",
  },
  accent: {
    scribble: "Bold asymmetric marker swoosh with a short returning stroke",
    underline: "Expressive curved double underline; no thin technical rule",
    none: "Typography alone; no underline or glow",
  },
  rules:
    "Choose a coherent combination for the concept, not random novelty. Opening and bridge titles must remain centered. Compare recent pacing, transition and music; do not keep using the same combination. Titles retain the same readable 2-line limits and Outfit font. Photography must leave the selected title zone quiet. Maximum message reading hold is not shortened for effects. Brand is an accent, never a legibility sacrifice. These are test hypotheses, not performance predictions.",
} as const;
export function reelTitleLayout(_direction?: ReelArtDirection) {
  // Legacy side-layout choices also render centered when rebuilding older drafts.
  return {
    x: 100,
    y: 470,
    width: 880,
    height: 430,
    entryOffset: 40,
    align: "center" as const,
  };
}

export function reelTiming(direction?: ReelArtDirection) {
  const pacing = direction?.pacing ?? "measured";
  const intro = pacing === "brisk" ? 4.6 : pacing === "reflective" ? 6 : 5.2;
  const duration = pacing === "brisk" ? 14 : pacing === "reflective" ? 18 : 16;
  const cardIn = intro + 0.4;
  const cardOut = intro + (pacing === "reflective" ? 3.8 : 3.2);
  const quote = cardOut + 0.4;
  return {
    intro,
    bridge: intro / 2,
    cardIn,
    cardOut,
    quote,
    cta: quote + 1.2,
    duration,
  };
}
export function reelDirectionCheckpoints(direction?: ReelArtDirection) {
  const t = reelTiming(direction);
  return [
    t.bridge / 2,
    t.bridge * 1.5,
    t.intro + 0.1,
    t.cardIn + 1,
    t.quote + 0.8,
    t.duration - 1.5,
  ].map((v) => Math.round(v * 100) / 100);
}
